/**
 * batch-71 verplichte-certificaten (FO-541, spec 241) - de pure regels.
 *
 * Geen I/O, geen DB. De drie vragen die er voor elke (medewerker, eis) toe doen:
 *
 *   bepaalGroepStatus        Wat is de beste status over de typen van de eis?
 *                            Eén geldig certificaat in de groep = heeft hem.
 *   bouwVerplichtStand       Wat is de stand (status, cursus, situatie)? Dit is de ENE
 *                            berekening achter de afgeleide tabel VerplichtCertificaatStand.
 *   bepaalVerplichtSignaal   Wat moet er nu verstuurd worden, en aan wie?
 *
 * `bepaalTypeStatus` blijft de enige plek die de status van één type berekent. Maanden in
 * dienst komen uit `dienstMaandenOpDatum` (seniorendagen), op de NL-kalenderdag.
 */

import type {
  CertificaatTypeStatus,
  OnboardingCertificaatDeadline,
  VerplichtCursusStatus,
  VerplichtSignaalOntvanger,
  VerplichtSignaalSoort,
  VerplichtSituatie,
} from '@streep/types'
import {
  bepaalTypeStatus,
  dagenTussenNlDagen,
  nlDagVanInstant,
  type BepaalTypeStatusOpts,
  type CertificaatDeelnameInput,
  type CertificaatVersieInput,
  type TypeStatusResultaat,
} from './certificaat-status'
import { alsNlDag } from './medewerker-selectie'
import { berekenCertificaatDeadline } from './onboarding-startworkflow'
import { dienstMaandenOpDatum } from './seniorendagen'

// ─── Groep-status ───────────────────────────────────────────────────────────

/**
 * Beste status eerst. Geldig wint van alles; een aangeleverd concept van een geplande cursus
 * (zelfde volgorde als `bepaalTypeStatus`); verlopen van n.v.t. zodat een vergeten verlopen
 * certificaat zichtbaar blijft ("nooit stil"); n.v.t. van ontbreekt, want wie een versie op
 * "niet meer nodig" zette bedoelde de eis.
 */
const GROEP_STATUS_VOLGORDE: readonly CertificaatTypeStatus[] = [
  'ACTIEF',
  'VERLOOPT_BINNENKORT',
  'WACHT_OP_CONTROLE',
  'IN_OPLEIDING',
  'GEPLAND',
  'WACHT_OP_CERTIFICAAT',
  'VERLOPEN',
  'NIET_VAN_TOEPASSING',
  'ONTBREEKT',
]

export interface GroepTypeInvoer<V extends CertificaatVersieInput, D extends CertificaatDeelnameInput> {
  certificateTypeId: string
  versies: V[]
  deelnames: D[]
}

export interface GroepStatusResultaat<V extends CertificaatVersieInput, D extends CertificaatDeelnameInput>
  extends TypeStatusResultaat<V, D> {
  /** Het type dat de status draagt; null als er geen typen zijn. */
  leidendTypeId: string | null
  perType: Array<{ certificateTypeId: string; resultaat: TypeStatusResultaat<V, D> }>
}

function statusRang(status: CertificaatTypeStatus): number {
  return GROEP_STATUS_VOLGORDE.indexOf(status)
}

/** Bij gelijke status: de langst geldige (null = verloopt nooit, wint), anders de vroegste planning. */
function beterBijGelijkeStatus<V extends CertificaatVersieInput, D extends CertificaatDeelnameInput>(
  a: TypeStatusResultaat<V, D>,
  b: TypeStatusResultaat<V, D>,
): boolean {
  if (a.status === 'ACTIEF' || a.status === 'VERLOOPT_BINNENKORT') {
    const eindeA = a.leidendeVersie?.expiresAt ?? null
    const eindeB = b.leidendeVersie?.expiresAt ?? null
    if (eindeB === null) return false
    if (eindeA === null) return true
    return nlDagVanInstant(eindeA) > nlDagVanInstant(eindeB)
  }
  if (a.status === 'GEPLAND') {
    const datumA = a.relevanteDeelname?.sessieDatum ?? null
    const datumB = b.relevanteDeelname?.sessieDatum ?? null
    if (datumA === null) return false
    if (datumB === null) return true
    return nlDagVanInstant(datumA) < nlDagVanInstant(datumB)
  }
  return false
}

/**
 * De beste status over de typen van een eis: één geldig certificaat in de groep voldoet
 * (spec 241 §A2, T4). Een eis op een exact type is hier gewoon een groep van één.
 */
export function bepaalGroepStatus<V extends CertificaatVersieInput, D extends CertificaatDeelnameInput>(
  typen: ReadonlyArray<GroepTypeInvoer<V, D>>,
  opts: BepaalTypeStatusOpts,
): GroepStatusResultaat<V, D> {
  const perType = typen.map((t) => ({
    certificateTypeId: t.certificateTypeId,
    resultaat: bepaalTypeStatus(t.versies, t.deelnames, opts),
  }))
  if (perType.length === 0) {
    return {
      status: 'ONTBREEKT',
      leidendeVersie: null,
      relevanteDeelname: null,
      dagenTotVerloop: null,
      leidendTypeId: null,
      perType,
    }
  }
  let beste = perType[0]!
  for (const kandidaat of perType.slice(1)) {
    const verschil = statusRang(kandidaat.resultaat.status) - statusRang(beste.resultaat.status)
    if (verschil < 0 || (verschil === 0 && beterBijGelijkeStatus(kandidaat.resultaat, beste.resultaat))) {
      beste = kandidaat
    }
  }
  return { ...beste.resultaat, leidendTypeId: beste.certificateTypeId, perType }
}

// ─── Geldt de eis voor deze medewerker? ─────────────────────────────────────

function normaliseerFunctie(titel: string): string {
  return titel.trim().toLowerCase()
}

/**
 * Spec 241 §A1 en A1: leeg = iedereen in dienst behalve KANTOOR. Gevuld: de medewerker valt
 * onder een genoemd type OF een genoemde functietitel (vergeleken zonder hoofdletters); wie
 * KANTOOR expliciet noemt, neemt KANTOOR dus wel mee.
 */
export function verplichteEisGeldtVoor(
  eis: { geldtVoorEmployeeTypes: readonly string[]; geldtVoorFunctietitels: readonly string[] },
  medewerker: { employeeType: string | null; functionTitle: string | null },
): boolean {
  const opType = eis.geldtVoorEmployeeTypes.length > 0
  const opFunctie = eis.geldtVoorFunctietitels.length > 0
  if (!opType && !opFunctie) return medewerker.employeeType !== 'KANTOOR'
  if (opType && medewerker.employeeType !== null && eis.geldtVoorEmployeeTypes.includes(medewerker.employeeType)) {
    return true
  }
  if (opFunctie && medewerker.functionTitle) {
    const functie = normaliseerFunctie(medewerker.functionTitle)
    return eis.geldtVoorFunctietitels.some((f) => normaliseerFunctie(f) === functie)
  }
  return false
}

/** Hele kalendermaanden in dienst op de NL-dag; null = startdatum onbekend (spec 241 §6.5, §6.7). */
export function maandenInDienstNl(startDate: Date | null, nu: Date): number | null {
  if (!startDate) return null
  return dienstMaandenOpDatum(alsNlDag(startDate), alsNlDag(nu))
}

// ─── Cursusstatus ───────────────────────────────────────────────────────────

/** Een afgeronde deelname van een opleiding die een type van de eis oplevert. */
export interface VerplichtAfgerondeDeelnameInput {
  deelnameId: string
  afgerondOp: Date | string
  opleidingNaam: string
}

export interface VerplichtCursusBeeld {
  cursusStatus: VerplichtCursusStatus
  cursusDatum: Date | string | null
  cursusNaam: string | null
  /** Alleen bij een afgeronde deelname: de bron van het bestaande uploadverzoek. */
  afgerondeDeelnameId: string | null
  afgerondOp: Date | string | null
}

/**
 * Wat is er met de cursus aan de hand, los van de certificaatstatus? `bepaalTypeStatus` kijkt
 * alleen naar de deelnames als er geen geldige versie is, en kent AFGEROND niet (die tellen
 * niet als pijplijn). Spec 241 §B4 onderscheidt juist "gevolgd, geen certificaat".
 */
export function bepaalVerplichtCursus<V extends CertificaatVersieInput & { issuedAt?: Date | string }, D extends CertificaatDeelnameInput & { opleidingNaam?: string }>(
  invoer: {
    groepStatus: CertificaatTypeStatus
    typen: ReadonlyArray<GroepTypeInvoer<V, D> & { afgerondeDeelnames?: readonly VerplichtAfgerondeDeelnameInput[] }>
    aanvraagOpen: boolean
  },
  opts: BepaalTypeStatusOpts,
): VerplichtCursusBeeld {
  const geen: VerplichtCursusBeeld = {
    cursusStatus: 'GEEN',
    cursusDatum: null,
    cursusNaam: null,
    afgerondeDeelnameId: null,
    afgerondOp: null,
  }

  // De planning, los van een eventueel geldig certificaat: "bepaalTypeStatus zonder versies".
  const pijplijn = invoer.typen.map((t) => bepaalTypeStatus<V, D>([], t.deelnames, opts))
  const gepland = pijplijn
    .filter((p) => (p.status === 'GEPLAND' || p.status === 'IN_OPLEIDING') && p.relevanteDeelname)
    .map((p) => p.relevanteDeelname as D)
    .sort((a, b) => {
      const dagA = a.sessieDatum ? nlDagVanInstant(a.sessieDatum) : ''
      const dagB = b.sessieDatum ? nlDagVanInstant(b.sessieDatum) : ''
      return dagA < dagB ? -1 : dagA > dagB ? 1 : 0
    })[0]

  // Gevolgd: een afgeronde deelname van NA het laatste bevestigde certificaat van de eis.
  const laatsteUitgifte = invoer.typen
    .flatMap((t) => t.versies)
    .filter((v) => (v.verificatieStatus ?? 'BEVESTIGD') === 'BEVESTIGD' && v.lifecycle !== 'GEANNULEERD' && v.issuedAt)
    .map((v) => nlDagVanInstant(v.issuedAt as Date | string))
    .sort()
    .at(-1)
  const afgerond = invoer.typen
    .flatMap((t) => t.afgerondeDeelnames ?? [])
    .filter((d) => !laatsteUitgifte || nlDagVanInstant(d.afgerondOp) > laatsteUitgifte)
    .sort((a, b) => (nlDagVanInstant(a.afgerondOp) > nlDagVanInstant(b.afgerondOp) ? -1 : 1))[0]

  if (invoer.groepStatus === 'WACHT_OP_CONTROLE') {
    return {
      ...geen,
      cursusStatus: 'GEVOLGD_WACHT_OP_CONTROLE',
      cursusDatum: afgerond?.afgerondOp ?? null,
      cursusNaam: afgerond?.opleidingNaam ?? null,
      afgerondeDeelnameId: afgerond?.deelnameId ?? null,
      afgerondOp: afgerond?.afgerondOp ?? null,
    }
  }
  if (gepland) {
    return {
      ...geen,
      cursusStatus: 'GEPLAND',
      cursusDatum: gepland.sessieDatum,
      cursusNaam: gepland.opleidingNaam ?? null,
    }
  }
  // Met een geldig certificaat is een eerder gevolgde cursus geen open eind meer.
  const heeftGeldig = invoer.groepStatus === 'ACTIEF' || invoer.groepStatus === 'VERLOOPT_BINNENKORT'
  if (!heeftGeldig) {
    if (afgerond) {
      return {
        cursusStatus: 'GEVOLGD_WACHT_OP_CERTIFICAAT',
        cursusDatum: afgerond.afgerondOp,
        cursusNaam: afgerond.opleidingNaam,
        afgerondeDeelnameId: afgerond.deelnameId,
        afgerondOp: afgerond.afgerondOp,
      }
    }
    const voorbij = pijplijn.find((p) => p.status === 'WACHT_OP_CERTIFICAAT' && p.relevanteDeelname)
    if (voorbij?.relevanteDeelname) {
      const d = voorbij.relevanteDeelname
      return {
        ...geen,
        cursusStatus: 'GEVOLGD_WACHT_OP_CERTIFICAAT',
        cursusDatum: d.sessieEindDatum ?? d.sessieDatum,
        cursusNaam: d.opleidingNaam ?? null,
      }
    }
  }
  if (invoer.aanvraagOpen && !heeftGeldig) return { ...geen, cursusStatus: 'AANGEVRAAGD' }
  return geen
}

// ─── Situatie ───────────────────────────────────────────────────────────────

export interface VerplichtSituatieInvoer {
  status: CertificaatTypeStatus
  cursusStatus: VerplichtCursusStatus
  maandenInDienst: number | null
  /** Null = nooit een uploadverzoek aan de medewerker (A5). */
  herinneringNaMaanden: number | null
  geldtVoorMedewerker: boolean
  /** HR zette de eis op n.v.t. voor deze medewerker. */
  nietVanToepassing: boolean
  /** De medewerker gaf aan de cursus nog niet gevolgd te hebben. */
  nogNietGevolgd: boolean
  /**
   * Er ligt een `CertificaatAanvraag` bij HR (portal of mailbox). `bepaalTypeStatus` kent die niet;
   * hier telt hij als "wacht op controle": dan vragen we de medewerker niet om hem nog een keer.
   */
  certificaatAanvraagOpen?: boolean
}

/**
 * De situatie uit spec 241 §B4. Gepland of gevolgd geeft nooit een "ontbreekt" (T5, T6):
 * die takken staan vóór de ONTBREEKT-tak en ook vóór VERLOPEN.
 */
export function bepaalVerplichtSituatie(s: VerplichtSituatieInvoer): VerplichtSituatie {
  if (!s.geldtVoorMedewerker || s.nietVanToepassing || s.status === 'NIET_VAN_TOEPASSING') {
    return 'NIET_VAN_TOEPASSING'
  }
  if (s.certificaatAanvraagOpen && s.status !== 'ACTIEF' && s.status !== 'VERLOOPT_BINNENKORT') {
    return 'WACHT_OP_CONTROLE'
  }
  switch (s.status) {
    case 'ACTIEF':
      return 'GELDIG'
    case 'VERLOOPT_BINNENKORT':
      return 'VERLOOPT'
    case 'GEPLAND':
      return 'GEPLAND'
    case 'IN_OPLEIDING':
      return 'IN_OPLEIDING'
    case 'WACHT_OP_CERTIFICAAT':
      return 'GEVOLGD_WACHT_UPLOAD'
    case 'WACHT_OP_CONTROLE':
      return 'WACHT_OP_CONTROLE'
    case 'VERLOPEN':
      return s.cursusStatus === 'GEVOLGD_WACHT_OP_CERTIFICAAT' ? 'GEVOLGD_WACHT_UPLOAD' : 'VERLOPEN'
    case 'ONTBREEKT': {
      if (s.cursusStatus === 'GEVOLGD_WACHT_OP_CERTIFICAAT') return 'GEVOLGD_WACHT_UPLOAD'
      if (s.maandenInDienst === null) return 'START_ONBEKEND'
      if (s.nogNietGevolgd || s.cursusStatus === 'AANGEVRAAGD') return 'ONTBREEKT_TE_PLANNEN'
      if (s.herinneringNaMaanden === null || s.maandenInDienst < s.herinneringNaMaanden) {
        return 'ONTBREEKT_TE_PLANNEN'
      }
      return 'ONTBREEKT_UPLOAD'
    }
  }
}

// ─── De stand: de ene berekening ────────────────────────────────────────────

export interface VerplichtSignaalLogInput {
  soort: VerplichtSignaalSoort
  aantal: number
  eersteOp: Date
  laatsteOp: Date
}

export interface VerplichtEisInvoer {
  deadlineType: OnboardingCertificaatDeadline
  herinneringNaMaanden: number | null
  geldtVoorEmployeeTypes: readonly string[]
  geldtVoorFunctietitels: readonly string[]
}

export interface VerplichtMedewerkerInvoer {
  employeeType: string | null
  functionTitle: string | null
  startDate: Date | null
}

export interface VerplichtOpvolgingInvoer {
  nietVanToepassing: boolean
  nvtReden: string | null
  snoozeTot: Date | null
  nogNietGevolgdOp: Date | null
}

export interface BouwVerplichtStandInvoer<
  V extends CertificaatVersieInput & { issuedAt?: Date | string; verificatieReden?: string | null },
  D extends CertificaatDeelnameInput & { opleidingNaam?: string },
> {
  eis: VerplichtEisInvoer
  medewerker: VerplichtMedewerkerInvoer
  typen: ReadonlyArray<GroepTypeInvoer<V, D> & { afgerondeDeelnames?: readonly VerplichtAfgerondeDeelnameInput[] }>
  aanvraagOpen: boolean
  /** Een `CertificaatAanvraag` voor een type van de eis ligt bij HR. */
  certificaatAanvraagOpen?: boolean
  opvolging: VerplichtOpvolgingInvoer | null
  /** De bestaande snooze op een type van de eis (`CertificaatSnooze`), de laatste. */
  typeSnoozeTot: Date | null
  signalen: readonly VerplichtSignaalLogInput[]
  nu: Date
  waarschuwingstermijnenDagen: number[]
}

export interface VerplichtStandBerekening {
  status: CertificaatTypeStatus
  situatie: VerplichtSituatie
  cursusStatus: VerplichtCursusStatus
  cursusDatum: Date | null
  cursusNaam: string | null
  leidendTypeId: string | null
  geldigTot: Date | null
  maandenInDienst: number | null
  deadline: Date | null
  laatsteSignaalOp: Date | null
  uploadVerzoekOp: Date | null
  uploadVerzoekAantal: number
  snoozeTot: Date | null
  nietVanToepassing: boolean
  nietVanToepassingReden: string | null
  afwijzingsReden: string | null
  /** Niet opgeslagen: voor de signalering. */
  afgerondeDeelnameId: string | null
  afgerondOp: Date | null
}

function alsDate(d: Date | string | null | undefined): Date | null {
  if (d === null || d === undefined) return null
  return typeof d === 'string' ? new Date(d) : d
}

function laatsteDatum(datums: ReadonlyArray<Date | null>): Date | null {
  let beste: Date | null = null
  for (const d of datums) {
    if (d && (!beste || d.getTime() > beste.getTime())) beste = d
  }
  return beste
}

/**
 * Bereken de stand van één (medewerker, eis). Dit is de ENE berekening: de meting, de hook na
 * een wijziging en de dry-run gebruiken allemaal deze functie.
 */
export function bouwVerplichtStand<
  V extends CertificaatVersieInput & { issuedAt?: Date | string; verificatieReden?: string | null },
  D extends CertificaatDeelnameInput & { opleidingNaam?: string },
>(invoer: BouwVerplichtStandInvoer<V, D>): VerplichtStandBerekening {
  const opts: BepaalTypeStatusOpts = {
    vandaag: invoer.nu,
    waarschuwingstermijnenDagen: invoer.waarschuwingstermijnenDagen,
  }
  const groep = bepaalGroepStatus(invoer.typen, opts)
  const cursus = bepaalVerplichtCursus(
    { groepStatus: groep.status, typen: invoer.typen, aanvraagOpen: invoer.aanvraagOpen },
    opts,
  )
  const maandenInDienst = maandenInDienstNl(invoer.medewerker.startDate, invoer.nu)
  const situatie = bepaalVerplichtSituatie({
    status: groep.status,
    cursusStatus: cursus.cursusStatus,
    maandenInDienst,
    herinneringNaMaanden: invoer.eis.herinneringNaMaanden,
    geldtVoorMedewerker: verplichteEisGeldtVoor(invoer.eis, invoer.medewerker),
    nietVanToepassing: invoer.opvolging?.nietVanToepassing ?? false,
    nogNietGevolgd: Boolean(invoer.opvolging?.nogNietGevolgdOp),
    certificaatAanvraagOpen: invoer.certificaatAanvraagOpen ?? false,
  })

  // De reden van de laatste afwijzing, zolang er geen geldig of nieuw concept overheen ligt.
  const afgewezen = invoer.typen
    .flatMap((t) => t.versies)
    .filter((v) => v.verificatieStatus === 'AFGEWEZEN' && v.lifecycle !== 'GEANNULEERD')
    .sort((a, b) => b.versie - a.versie)[0]
  const toonAfwijzing =
    situatie === 'ONTBREEKT_UPLOAD' || situatie === 'ONTBREEKT_TE_PLANNEN' || situatie === 'VERLOPEN' || situatie === 'GEVOLGD_WACHT_UPLOAD' || situatie === 'START_ONBEKEND'

  const uploadLog = invoer.signalen.find((s) => s.soort === 'UPLOAD_VERZOEK')
  const snoozeTot = laatsteDatum([invoer.opvolging?.snoozeTot ?? null, invoer.typeSnoozeTot])

  return {
    status: groep.status,
    situatie,
    // Een aanvraag bij HR is voor de cursus hetzelfde als een concept: gevolgd, wacht op controle.
    cursusStatus: situatie === 'WACHT_OP_CONTROLE' ? 'GEVOLGD_WACHT_OP_CONTROLE' : cursus.cursusStatus,
    cursusDatum: alsDate(cursus.cursusDatum),
    cursusNaam: cursus.cursusNaam,
    leidendTypeId: groep.leidendeVersie || groep.relevanteDeelname ? groep.leidendTypeId : null,
    // Een concept is nog niet geldig: zijn datum hoort niet in de stand.
    geldigTot: groep.status === 'WACHT_OP_CONTROLE' ? null : alsDate(groep.leidendeVersie?.expiresAt ?? null),
    maandenInDienst,
    deadline: invoer.medewerker.startDate
      ? berekenCertificaatDeadline(invoer.medewerker.startDate, invoer.eis.deadlineType)
      : null,
    laatsteSignaalOp: laatsteDatum(invoer.signalen.map((s) => s.laatsteOp)),
    uploadVerzoekOp: uploadLog?.eersteOp ?? null,
    uploadVerzoekAantal: uploadLog?.aantal ?? 0,
    snoozeTot,
    nietVanToepassing: invoer.opvolging?.nietVanToepassing ?? false,
    nietVanToepassingReden: invoer.opvolging?.nvtReden ?? null,
    afwijzingsReden: toonAfwijzing ? (afgewezen?.verificatieReden ?? null) : null,
    afgerondeDeelnameId: cursus.afgerondeDeelnameId,
    afgerondOp: alsDate(cursus.afgerondOp),
  }
}

// ─── Het signaal ────────────────────────────────────────────────────────────

export interface VerplichtSignaalInstelling {
  /** De eerste verzendronde wacht op het HR-akkoord op het dry-run-rapport (spec 241 §8). */
  uploadVerzoekenActief: boolean
  herhaalDagen: number
  maxHerinneringen: number
}

export interface VerplichtSignaalInvoer {
  situatie: VerplichtSituatie
  snoozeTot: Date | null
  /** Uit dienst per een datum in de toekomst: niemand meer lastigvallen. */
  geplandUitDienst: boolean
  /** Gekoppeld account met portal-toegang. Zonder: het verzoek gaat naar HR en de teamleider (§6.6). */
  bereikbaarViaPortal: boolean
  /** Alleen bij een afgeronde deelname: de teller van het bestaande uploadverzoek. */
  afgerondOp: Date | null
  signalen: readonly VerplichtSignaalLogInput[]
  nu: Date
}

export interface VerplichtSignaalBesluit {
  /** Wat er uit moet; null = niets. */
  soort: VerplichtSignaalSoort | null
  ontvanger: VerplichtSignaalOntvanger | null
  /** 0 = het eerste verzoek; vanaf 1 een herinnering (de hoeveelste). */
  herinneringNummer: number
  /** Dit signaal gaat pas uit na het HR-akkoord: de dry-run telt het, de meting verstuurt het niet. */
  wachtOpAkkoord: boolean
  /**
   * Hoort bij de weekbundel: de dagelijkse meting verstuurt dit niet, de bundel van maandag
   * 08:00 wel.
   */
  inWeekbundel: boolean
  /**
   * De melding loopt al via een bestaand pad (`check-expiries`, de upload zelf, de dag-14-cron):
   * deze functie noemt het signaal, de meting verstuurt het niet nog een keer.
   */
  bestaandKanaal: boolean
  /** Voor de log en de dry-run. */
  reden: string
}

const GEEN: Omit<VerplichtSignaalBesluit, 'reden'> = {
  soort: null,
  ontvanger: null,
  herinneringNummer: 0,
  wachtOpAkkoord: false,
  inWeekbundel: false,
  bestaandKanaal: false,
}

function geenSignaal(reden: string, bestaandKanaal = false): VerplichtSignaalBesluit {
  return { ...GEEN, reden, bestaandKanaal }
}

function dagenSinds(d: Date, nu: Date): number {
  return dagenTussenNlDagen(nlDagVanInstant(d), nlDagVanInstant(nu))
}

function logVan(
  signalen: readonly VerplichtSignaalLogInput[],
  soort: VerplichtSignaalSoort,
): VerplichtSignaalLogInput | undefined {
  return signalen.find((s) => s.soort === soort)
}

/**
 * De situatie-afhankelijke signalen van spec 241 §B4. Idempotent per (medewerker, eis, soort):
 * het logboek bepaalt of en wanneer het volgende signaal aan de beurt is.
 */
export function bepaalVerplichtSignaal(
  stand: VerplichtSignaalInvoer,
  instelling: VerplichtSignaalInstelling,
): VerplichtSignaalBesluit {
  const { situatie, nu } = stand
  if (situatie === 'NIET_VAN_TOEPASSING') return geenSignaal('Niet van toepassing')
  if (situatie === 'GELDIG') return geenSignaal('Certificaat is geldig')
  if (stand.snoozeTot && stand.snoozeTot.getTime() > nu.getTime()) return geenSignaal('Gesnoozed door HR')
  if (stand.geplandUitDienst) return geenSignaal('Gaat uit dienst')

  switch (situatie) {
    case 'VERLOOPT':
    case 'VERLOPEN':
      return geenSignaal('Bestaande verloop-meldingen (check-expiries)', true)
    case 'GEPLAND':
    case 'IN_OPLEIDING':
      return geenSignaal('Cursus staat gepland: geen ontbreekt-signaal (OPLEIDING_HERINNERING zoals nu)')
    case 'WACHT_OP_CONTROLE':
      // De upload meldt het zelf, check-expiries herhaalt het wekelijks; de medewerker krijgt niets.
      return { ...GEEN, soort: null, ontvanger: 'HR', bestaandKanaal: true, reden: 'HR-signaal CERTIFICAAT_CONTROLE_GEVRAAGD (bestaand)' }
    case 'GEVOLGD_WACHT_UPLOAD':
      return signaalGevolgd(stand, instelling)
    // Ook de weekbundel wacht op het HR-akkoord: vóór dat akkoord kan de lijst nog de voorbeelden zijn
    // en zou elke teamleider "BHV te plannen" voor zijn hele team krijgen (spec 241 §8).
    case 'START_ONBEKEND':
      return {
        ...GEEN,
        soort: 'START_ONBEKEND',
        ontvanger: 'HR',
        inWeekbundel: true,
        wachtOpAkkoord: !instelling.uploadVerzoekenActief,
        reden: 'Startdatum onbekend: HR-signaal, geen medewerker-verzoek',
      }
    case 'ONTBREEKT_TE_PLANNEN':
      return {
        ...GEEN,
        soort: 'TE_PLANNEN',
        ontvanger: 'HR_TL',
        inWeekbundel: true,
        wachtOpAkkoord: !instelling.uploadVerzoekenActief,
        reden: 'Nog te plannen: weekbundel aan HR en teamleider',
      }
    case 'ONTBREEKT_UPLOAD':
      return signaalOntbreektUpload(stand, instelling)
  }
}

/** ONTBREEKT, lang genoeg in dienst: verzoek, herinneringen, daarna HR en teamleider. */
function signaalOntbreektUpload(
  stand: VerplichtSignaalInvoer,
  instelling: VerplichtSignaalInstelling,
): VerplichtSignaalBesluit {
  const log = logVan(stand.signalen, 'UPLOAD_VERZOEK')
  const verstuurd = log?.aantal ?? 0
  const ontvanger: VerplichtSignaalOntvanger = stand.bereikbaarViaPortal ? 'MEDEWERKER' : 'HR_TL'
  const wachtOpAkkoord = !instelling.uploadVerzoekenActief

  if (verstuurd === 0) {
    return { ...GEEN, soort: 'UPLOAD_VERZOEK', ontvanger, wachtOpAkkoord, reden: 'Eerste uploadverzoek' }
  }
  const sindsLaatste = log ? dagenSinds(log.laatsteOp, stand.nu) : Number.POSITIVE_INFINITY
  if (sindsLaatste < instelling.herhaalDagen) return geenSignaal('Uploadverzoek staat open, herinnering nog niet aan de beurt')
  if (verstuurd <= instelling.maxHerinneringen) {
    return {
      ...GEEN,
      soort: 'UPLOAD_VERZOEK',
      ontvanger,
      herinneringNummer: verstuurd,
      wachtOpAkkoord,
      reden: `Herinnering ${verstuurd} van ${instelling.maxHerinneringen}`,
    }
  }
  const hrLog = logVan(stand.signalen, 'NIET_AANGELEVERD')
  if (hrLog && dagenSinds(hrLog.laatsteOp, stand.nu) < instelling.herhaalDagen) {
    return geenSignaal('HR en teamleider zijn al gemeld, volgende melding nog niet aan de beurt')
  }
  return { ...GEEN, soort: 'NIET_AANGELEVERD', ontvanger: 'HR_TL', wachtOpAkkoord, reden: 'Herinneringen op: naar HR en teamleider' }
}

/**
 * GEVOLGD, geen certificaat. De eerste herinnering (dag 14) stuurt de bestaande cron
 * `certificaat-upload-herinnering`; deze functie herhaalt elke 14 dagen tot 3 keer in totaal en
 * meldt daarna HR met het bestaande CERTIFICAAT_NIET_OPGEPAKT (T6).
 */
function signaalGevolgd(
  stand: VerplichtSignaalInvoer,
  instelling: VerplichtSignaalInstelling,
): VerplichtSignaalBesluit {
  if (!stand.afgerondOp) {
    return geenSignaal('Sessie voorbij, deelname nog niet afgerond: bestaand CERTIFICAAT_WACHT_OP_UPLOAD aan HR', true)
  }
  const dagen = dagenSinds(stand.afgerondOp, stand.nu)
  const log = logVan(stand.signalen, 'GEVOLGD_HERINNERING')
  const verstuurd = log?.aantal ?? 0
  const sindsLaatste = log ? dagenSinds(log.laatsteOp, stand.nu) : Number.POSITIVE_INFINITY
  // Nummer 1 is de dag-14-herinnering van de bestaande cron (die telt niet in ons logboek).
  const volgendeNummer = verstuurd + 2
  if (volgendeNummer <= instelling.maxHerinneringen) {
    if (dagen < instelling.herhaalDagen * volgendeNummer || sindsLaatste < instelling.herhaalDagen) {
      return geenSignaal(`Herinnering ${volgendeNummer} nog niet aan de beurt`, true)
    }
    return {
      ...GEEN,
      soort: 'GEVOLGD_HERINNERING',
      ontvanger: stand.bereikbaarViaPortal ? 'MEDEWERKER' : 'HR_TL',
      herinneringNummer: volgendeNummer,
      wachtOpAkkoord: !instelling.uploadVerzoekenActief,
      reden: `Herinnering ${volgendeNummer} van ${instelling.maxHerinneringen} na de gevolgde cursus`,
    }
  }
  const hrLog = logVan(stand.signalen, 'NIET_OPGEPAKT')
  if (dagen < instelling.herhaalDagen * (instelling.maxHerinneringen + 1) || sindsLaatste < instelling.herhaalDagen) {
    return geenSignaal('Nog binnen de laatste herinneringstermijn')
  }
  if (hrLog && dagenSinds(hrLog.laatsteOp, stand.nu) < instelling.herhaalDagen) {
    return geenSignaal('HR is al gemeld, volgende melding nog niet aan de beurt')
  }
  return {
    ...GEEN,
    soort: 'NIET_OPGEPAKT',
    ontvanger: 'HR',
    wachtOpAkkoord: !instelling.uploadVerzoekenActief,
    reden: 'Herinneringen op: CERTIFICAAT_NIET_OPGEPAKT aan HR',
  }
}

// ─── Portal: wat mag de medewerker hier? ────────────────────────────────────

/** Uploaden kan zolang er geen geldig certificaat of concept in behandeling ligt. */
export function verplichtMagUploaden(situatie: VerplichtSituatie): boolean {
  return (
    situatie === 'ONTBREEKT_UPLOAD' ||
    situatie === 'ONTBREEKT_TE_PLANNEN' ||
    situatie === 'START_ONBEKEND' ||
    situatie === 'GEVOLGD_WACHT_UPLOAD' ||
    situatie === 'VERLOPEN' ||
    situatie === 'VERLOOPT'
  )
}

/**
 * Een cursus aanvragen kan als er nog geen planning of aanvraag loopt en er niets gevolgd is. Niet
 * bij een certificaat dat nog geldig is (VERLOOPT): dat weigert de API ook (`meldCursusNogNietGevolgd`).
 */
export function verplichtMagCursusAanvragen(
  situatie: VerplichtSituatie,
  cursusStatus: VerplichtCursusStatus,
): boolean {
  if (cursusStatus !== 'GEEN') return false
  return (
    situatie === 'ONTBREEKT_UPLOAD' ||
    situatie === 'ONTBREEKT_TE_PLANNEN' ||
    situatie === 'START_ONBEKEND' ||
    situatie === 'VERLOPEN'
  )
}

/**
 * Staat er een uploadverzoek open? De kaart op het dashboard en de KPI tellen hierop. Niet na "nog
 * niet gevolgd" (T2: dan loopt de aanvraag en is de teller teruggezet) en niet als er geen verzoek
 * uit is gegaan: vóór het HR-akkoord staat er dus nog geen kaart.
 */
export function isUploadVerzoekOpen(rij: {
  situatie: VerplichtSituatie
  cursusStatus: VerplichtCursusStatus
  uploadVerzoekAantal: number
}): boolean {
  if (rij.uploadVerzoekAantal <= 0) return false
  if (rij.situatie === 'ONTBREEKT_UPLOAD' || rij.situatie === 'START_ONBEKEND') return true
  return rij.situatie === 'ONTBREEKT_TE_PLANNEN' && rij.cursusStatus === 'GEEN'
}
